import { Controller } from "@hotwired/stimulus"

// Keep the original form and files in place on failure. The request key makes a
// retry safe when the server committed but the mobile connection lost its reply.
export default class extends Controller {
  static targets = ["status", "submit", "partial", "reason"]

  connect() {
    this.outcomeChanged()
    this.markDirty = () => { this.dirty = true }
    this.warnBeforeUnload = (event) => {
      if (!this.dirty) return
      event.preventDefault()
      event.returnValue = ""
    }
    this.element.addEventListener("input", this.markDirty)
    window.addEventListener("beforeunload", this.warnBeforeUnload)
  }

  disconnect() {
    this.element.removeEventListener("input", this.markDirty)
    window.removeEventListener("beforeunload", this.warnBeforeUnload)
  }

  outcomeChanged() {
    if (!this.element.elements.outcome) return
    const outcome = this.element.elements.outcome.value
    this.partialTarget.hidden = outcome !== "partial"
    this.reasonTarget.hidden = outcome === "delivered"
    this.element.elements.reason.required = outcome !== "delivered"
  }

  async submit(event) {
    event.preventDefault()
    if (this.sending || !this.element.reportValidity()) return
    const data = new FormData(this.element)
    const files = [...data.values()].filter(value => value instanceof File && value.size > 0)
    if (files.some(file => file.size > 10 * 1024 * 1024) || data.getAll("photos[]").filter(file => file.size > 0).length > 6) {
      this.statusTarget.textContent = "Choose up to 6 photos, with each file no larger than 10 MB."
      return
    }
    this.sending = true
    this.submitTarget.disabled = true
    this.statusTarget.textContent = "Saving delivery and uploading evidence…"
    try {
      const response = await fetch(this.element.action, {
        method: "POST", body: data, credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
      })
      const json = await response.json().catch(() => null)
      if (!response.ok || !json?.attempt_id) {
        this.statusTarget.textContent = json?.error || "Could not save. Check your connection or sign-in, then retry. Your files are still selected."
        this.showRetryLabel()
        return
      }
      this.statusTarget.textContent = "Delivery outcome and evidence saved."
      this.dirty = false
      window.location.reload()
    } catch {
      this.statusTarget.textContent = "Connection lost. Keep this page open and retry when connected. Your files are still selected."
      this.showRetryLabel()
    } finally {
      this.sending = false
      this.submitTarget.disabled = false
    }
  }

  showRetryLabel() {
    if (this.submitTarget.tagName === "INPUT") this.submitTarget.value = "Retry saving"
    else this.submitTarget.textContent = "Retry saving"
  }
}
